import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiPost, apiPatch, operationsClient } from '@/lib/api';
import { schedulerEndpoints } from '@/lib/api/endpoints';
import { schedulerKeys } from '@/lib/queryKeys';
import type { SchedulerTechnician } from '@/types/scheduler';

const MAX_PHOTO_BYTES = 2 * 1024 * 1024; // 2 MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png'];

/**
 * Upload a technician profile photo. The browser uploads the image straight to
 * S3 via a presigned PUT (the app's standard photo pattern, see
 * `useContactFiles`), then PATCHes the returned key onto the technician so the
 * backend persists it and returns a fresh signed `photoUrl`.
 */
export function useUploadTechnicianPhoto(technicianId: string) {
  const queryClient = useQueryClient();

  return useApiMutation<SchedulerTechnician, File>({
    mutationFn: async (file) => {
      if (!ALLOWED_TYPES.includes(file.type)) {
        throw new Error('Only JPEG or PNG images are allowed');
      }
      if (file.size > MAX_PHOTO_BYTES) {
        throw new Error('Image exceeds the 2 MB limit');
      }

      const { uploadUrl, fileKey } = await apiPost<{
        uploadUrl: string;
        fileKey: string;
      }>(operationsClient, schedulerEndpoints.technicianPhotoUploadUrl(technicianId), {
        fileName: file.name,
        contentType: file.type,
        fileSize: file.size,
      });

      const response = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });
      if (!response.ok) {
        throw new Error(`Upload failed (${response.status})`);
      }

      return apiPatch<SchedulerTechnician>(
        operationsClient,
        schedulerEndpoints.technician(technicianId),
        { photoKey: fileKey },
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: schedulerKeys.detail(technicianId) });
      void queryClient.invalidateQueries({ queryKey: schedulerKeys.technicians() });
      toast.success('Photo updated');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to upload photo');
    },
  });
}
